/**
 * FilterSection Component
 * 라이더 출금제한 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { ContractStatusOption } from '../types/riderAccounting.types';
import '@/shared/styles/search-form.css';

const WITHDRAWAL_STATUS_OPTIONS: SelectOption[] = [
    { value: 'N', label: '출금허용' },
    { value: 'Y', label: '출금제한' },
];

interface PartnerName {
    suppId: string;
    suppName: string;
}

interface FilterSectionProps {
    userType: string | null;
    searchPartnerId: string;
    searchRiderKeyword: string;
    searchContractStatus: string;
    searchWithdrawalStatus: string;
    partnerNames: PartnerName[] | null;
    contractStatusCodes: ContractStatusOption[];
    contractStatusLoading: boolean;
    loading: boolean;
    onPartnerIdChange: (value: string) => void;
    onRiderKeywordChange: (value: string) => void;
    onContractStatusChange: (value: string) => void;
    onWithdrawalStatusChange: (value: string) => void;
    onSearch: () => void;
    onReset: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         userType,
                                                         searchPartnerId,
                                                         searchRiderKeyword,
                                                         searchContractStatus,
                                                         searchWithdrawalStatus,
                                                         partnerNames,
                                                         contractStatusCodes,
                                                         contractStatusLoading,
                                                         loading,
                                                         onPartnerIdChange,
                                                         onRiderKeywordChange,
                                                         onContractStatusChange,
                                                         onWithdrawalStatusChange,
                                                         onSearch,
                                                         onReset,
                                                     }) => {
    const partnerOptions: SelectOption[] = useMemo(() => {
        if (!partnerNames) return [];
        return partnerNames.map((partner) => ({
            value: partner.suppId,
            label: partner.suppName,
        }));
    }, [partnerNames]);

    const contractStatusOptions: SelectOption[] = useMemo(
        () => contractStatusCodes.map((s) => ({ value: s.code, label: s.name })),
        [contractStatusCodes]
    );

    const formClassName = userType === 'HEADQUARTERS'
        ? 'search-form rider-accounting-search-form-hq'
        : 'search-form rider-accounting-search-form';

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className={formClassName}>
                    {userType === 'HEADQUARTERS' && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={partnerOptions}
                                    value={searchPartnerId}
                                    onChange={onPartnerIdChange}
                                    placeholder="협력사 검색..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 라이더명 / 라이더ID */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-user" />
                                라이더명 / 라이더ID
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={searchRiderKeyword}
                                onChange={(e) => onRiderKeywordChange(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
                                placeholder="라이더명 또는 라이더ID 입력..."
                            />
                        </div>
                    </div>

                    {/* 계약상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-file-contract" />
                                계약상태
                            </label>
                            <SearchableSelect
                                options={contractStatusOptions}
                                value={searchContractStatus}
                                onChange={onContractStatusChange}
                                placeholder="계약상태 선택..."
                                showAllOption={true}
                                allOptionLabel="전체"
                                disabled={contractStatusLoading}
                            />
                        </div>
                    </div>

                    {/* 출금상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-money-bill-wave" />
                                출금상태
                            </label>
                            <SearchableSelect
                                options={WITHDRAWAL_STATUS_OPTIONS}
                                value={searchWithdrawalStatus}
                                onChange={onWithdrawalStatusChange}
                                placeholder="출금상태 선택..."
                                showAllOption={true}
                                allOptionLabel="전체"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button
                            className="btn btn-secondary"
                            onClick={onReset}
                            disabled={loading}
                        >
                            <i className="fas fa-redo" />
                            초기화
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                            disabled={loading}
                        >
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
